import type { Signal } from '@angular/core';

/**
 * Port: surfaces the current authenticated account for the shell (sidebar user menu).
 *
 * The concrete adapter is IAM-coupled — it runs the `iamMeAccount` query, which belongs to the
 * IAM bounded context — and therefore lives in APP code, wired via DI in `aurora.provider.ts`.
 * The framework owns only this contract, so shell consumers depend on the abstraction rather than
 * on IAM data-access, keeping `@aurora` free of an `@aurora → app` dependency. Mirrors the
 * `AuthenticationService` port + adapter pattern.
 *
 * ## Permission surface (extended for permission-gated navigation)
 *
 * `permissions`, `permissionsLoaded`, and `hasPermission()` are the framework's authorization
 * surface. They mirror the backend `dPermissions.all` convention but remain IAM-free in `@aurora`:
 * only the population of these signals (reading `iamMeAccount.dPermissions`) is IAM-coupled and
 * lives in the app-side adapter.
 *
 * ### Usage example (sidebar filtering)
 * ```typescript
 * const visible = computed(() => {
 *   if (!account.permissionsLoaded()) return []; // wait out the async window
 *   return items.filter(item => !item.permission || account.hasPermission(item.permission));
 * });
 * ```
 *
 * ### Usage example (permission guard)
 * ```typescript
 * if (!account.permissionsLoaded()) await firstValueFrom(toObservable(account.permissionsLoaded).pipe(filter(Boolean)));
 * return account.hasPermission('hub.launchpad.access') ? true : router.parseUrl('/');
 * ```
 */
export abstract class CurrentAccountService {
  /** Display name (name + surname, or username), blank until `load()` resolves. */
  abstract readonly displayName: Signal<string>;
  /** Account email, blank until `load()` resolves. */
  abstract readonly email: Signal<string>;
  /**
   * Flattened permission strings the session holds (mirrors backend `dPermissions.all`).
   * Empty array until `load()` resolves. `readonly` to prevent consumer mutation of the
   * session permission set.
   */
  abstract readonly permissions: Signal<readonly string[]>;
  /**
   * True once `load()` has settled (success OR best-effort failure). Lets consumers — the
   * sidebar and the permission guard — wait out the async post-login window before acting on
   * the permission set. Without this flag, an empty `permissions()` is ambiguous: it could
   * mean "not yet loaded" or "genuinely no permissions".
   */
  abstract readonly permissionsLoaded: Signal<boolean>;
  /**
   * Pure derivation over `permissions()`. Returns `false` while not loaded or when the
   * permission is absent. Callers wrap calls in their own `computed()` for reactivity.
   *
   * @param permission - The permission key to check (e.g. `'hub.launchpad.access'`).
   * @returns `true` only when `permissionsLoaded()` is `true` AND `permissions()` contains
   *          the requested key.
   */
  abstract hasPermission(permission: string): boolean;
  /**
   * The RAW `defaultRedirection` of the first role of the session that carries a non-null value,
   * in the order the server delivers the roles (`null` when no role carries one, or before
   * `load()` resolves). Exposed unvalidated: deciding whether the value may be navigated to
   * belongs to the landing resolution (capability `frontend-role-ui-preferences`), not to this
   * port — a port that pre-validated would silently swallow a misconfigured value instead of
   * letting the resolver fall through to the next precedence step.
   */
  abstract readonly defaultRedirection: Signal<string | null>;
  /**
   * Logical OR of every role's `hasHiddenVerticalNavigation`, with a `null` flag treated as
   * `false`. `false` before `load()` resolves and when no role of the session sets it.
   */
  abstract readonly hasHiddenVerticalNavigation: Signal<boolean>;
  /**
   * Readiness latch for the CURRENT `load()` invocation — distinct from `permissionsLoaded`.
   * Starts `false`, is reset to `false` at the START of every `load()` call (re-arms), and
   * becomes `true` when that invocation settles (success OR failure). The re-arm is load-bearing:
   * on an impersonation swap `load()` runs while a PREVIOUS account is already loaded, and a
   * consumer computing the landing route must be able to tell "not loaded yet" apart from
   * "loaded for a previous session" — a one-way flag (the shape `permissionsLoaded` has) cannot
   * express that distinction. `permissionsLoaded` keeps its existing one-way semantics
   * unconditionally: the sidebar and the permission guard depend on it never resetting mid-session.
   */
  abstract readonly loaded: Signal<boolean>;
  /** Id of the session's account, `null` until `load()` resolves (and without an IAM adapter). */
  abstract readonly accountId: Signal<string | null>;
  /**
   * The user's photo as an `<img src>` value (a `data:` URI), `null` when the user has none, the
   * load has not resolved, or there is no IAM adapter.
   */
  abstract readonly avatarSrc: Signal<string | null>;
  /** Initials for the avatar fallback (`'AL'`), blank until `load()` resolves. */
  abstract readonly initials: Signal<string>;
  /**
   * Opens the account profile (the IAM-owned dialog) for the session's account. The shell calls
   * this from the user menu without importing any IAM code; the anonymous adapter does nothing.
   */
  abstract openProfile(): void;
  /** Best-effort load of the current account; failures leave the signals blank. */
  abstract load(): void;
}
